import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useAuth } from '../../../../util/authenticationProvider';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../../../config';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';


export default function LocationsManagement() {
    const { t } = useTranslation();
    const { tokens } = useAuth();
    const [name, setName] = useState('');
    const [reviewLink, setreviewLink] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);

    // Edit dialog state
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editReviewLink, setEditReviewLink] = useState('');
    const [editError, setEditError] = useState('');
    const [editMessage, setEditMessage] = useState('');

    // Delete confirmation dialog state
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${config.apiUrl}/api/location/all`, {
                headers: {
                    'Authorization': `Bearer ${tokens?.accessToken ?? ''}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setLocations(data.content || []);
            }
        } catch (err) {
            // Optionally handle error
        }
        setLoading(false);
    }, [tokens?.accessToken]);

    useEffect(() => {
        if (!tokens?.accessToken) {
            setLocations([]);
            return;
        }
        fetchLocations();
    }, [fetchLocations, tokens?.accessToken]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setMessage('');
        setError('');
        try {
            const response = await fetch(`${config.apiUrl}/api/location/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens?.accessToken ?? ''}`
                },
                body: JSON.stringify({ name, reviewLink }),
            });
            if (response.ok) {
                setMessage('Location created successfully!');
                setName('');
                setreviewLink('');
                fetchLocations();
            } else {
                setError('Failed to create location.');
            }
        } catch (error) {
            setError('Error submitting form.');
        }
    };

    const handleEditOpen = (location: any) => {
        setEditId(location.id);
        setEditName(location.name);
        setEditReviewLink(location.reviewLink || '');
        setEditOpen(true);
        setEditMessage('');
        setEditError('');
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setEditId(null);
        setEditName('');
        setEditReviewLink('');
        setEditError('');
    };

    const handleEditSubmit = async () => {
        setEditMessage('');
        setEditError('');
        if (!editId) {
            setEditError('No location selected.');
            return;
        }
        try {
            const response = await fetch(`${config.apiUrl}/api/location/update/${encodeURIComponent(editId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens?.accessToken ?? ''}`
                },
                body: JSON.stringify({
                    name: editName,
                    reviewLink: editReviewLink
                }),
            });
            if (response.ok) {
                fetchLocations();
                handleEditClose();
                setEditMessage(t('locations.editTitle'));
            } else {
                setEditError('Failed to update location.');
            }
        } catch (error) {
            setEditError('Error updating location.');
        }
    };

    const handleDeleteConfirmOpen = (id: number) => {
        setDeleteId(id);
        setDeleteOpen(true);
    };

    const handleDeleteConfirmClose = () => {
        setDeleteOpen(false);
        setDeleteId(null);
    };

    const handleDelete = async () => {
        if (deleteId === null) return;
        setMessage('');
        setError('');
        try {
            const response = await fetch(`${config.apiUrl}/api/location/delete/${deleteId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${tokens?.accessToken ?? ''}`
                }
            });
            if (response.ok) {
                setMessage('Location deleted successfully!');
                fetchLocations();
            } else {
                setError('Failed to delete location.');
            }
        } catch (error) {
            setError('Error deleting location.');
        }
        handleDeleteConfirmClose();
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('locations.name'), flex: 1 },
        { field: 'reviewLink', headerName: t('locations.link'), flex: 1 },
        {
            field: 'actions',
            headerName: t('locations.actions'),
            width: 130,
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label={t('locations.edit')}
                        color="primary"
                        onClick={() => handleEditOpen(params.row)}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        aria-label={t('locations.delete')}
                        color="error"
                        onClick={() => handleDeleteConfirmOpen(params.row.id)}
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    return (
        <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                    {t('locations.title')}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        label={t('locations.nameLabel')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label={t('locations.linkLabel')}
                        type="url"
                        value={reviewLink}
                        onChange={e => setreviewLink(e.target.value)}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        {t('locations.create')}
                    </Button>
                </form>
                {message && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {t('locations.createSuccess')}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('locations.error')}
                    </Alert>
                )}
            </Paper>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {t('locations.all')}
                </Typography>
                <div style={{ height: 400, width: '100%' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={locations}
                            columns={columns}
                            getRowId={(row) => row.id}
                        />
                    )}
                </div>
                {editMessage && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {editMessage}
                    </Alert>
                )}
            </Paper>
            <Dialog open={editOpen} onClose={handleEditClose}>
                <DialogTitle>{t('locations.editTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        label={t('locations.nameLabel')}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label={t('locations.linkLabel')}
                        type="url"
                        value={editReviewLink}
                        onChange={e => setEditReviewLink(e.target.value)}
                        required
                        fullWidth
                        margin="normal"
                    />
                    {editError && <Alert severity="error" sx={{ mt: 1 }}>{editError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>{t('general.Cancel')}</Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        color="primary"
                    >
                        {t('general.Save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteOpen} onClose={handleDeleteConfirmClose}>
                <DialogTitle>{t('locations.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('locations.deleteConfirm')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteConfirmClose}>{t('general.No')}</Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                    >
                        {t('general.Yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
